'use client';

import { useEffect } from 'react';

import { SIGNUP_CONVERSION_COOKIE } from '~/lib/marketing/signup-conversion';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Fires the Meta Pixel + Google Ads signup conversion events exactly once,
 * when the one-time `signup_conversion` cookie is present (set server-side at
 * the verified-new-signup moment). Reads and clears the cookie immediately so
 * the conversion never double-fires on subsequent page loads.
 *
 * Mounted in the root layout. No-ops when the cookie is absent, and when the
 * pixels aren't loaded (non-production deploys) the optional-chained calls are
 * skipped. The Google Ads conversion only fires once its conversion label is
 * configured via NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_CONVERSION.
 */
export function SignupConversionTracker() {
  useEffect(() => {
    const hasCookie = document.cookie
      .split('; ')
      .some((entry) => entry.startsWith(`${SIGNUP_CONVERSION_COOKIE}=`));

    if (!hasCookie) return;

    // Clear immediately so the conversion fires only once.
    document.cookie = `${SIGNUP_CONVERSION_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;

    // Meta Pixel — standard registration-complete event.
    window.fbq?.('track', 'CompleteRegistration');

    // Google Ads — needs the per-conversion send_to label (AW-XXXX/label).
    const sendTo = process.env.NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_CONVERSION;

    if (sendTo) {
      window.gtag?.('event', 'conversion', { send_to: sendTo });
    }
  }, []);

  return null;
}
